import { Component } from '@theme/component';
import { getWishlist, toggleWishlist } from '@theme/wishlist';
import { formatMoney } from '@theme/money-formatting';

/**
 * Renders the current browser-local wishlist as a simple product grid,
 * fetching each product's public JSON on demand (no server-side knowledge
 * of localStorage is possible, so this has to happen client-side).
 */
export class WishlistPageList extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();
    this.render();
    document.addEventListener('wishlist:change', this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wishlist:change', this.#onChange);
  }

  #onChange = () => this.render();

  async render() {
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const handles = getWishlist();

    if (!grid || !empty) return;

    if (handles.length === 0) {
      grid.replaceChildren();
      empty.hidden = false;
      return;
    }

    empty.hidden = true;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );

    grid.replaceChildren();

    for (const product of products) {
      if (!product) continue;
      grid.append(this.#buildCard(product));
    }
  }

  /** @param {any} product */
  #buildCard(product) {
    const item = document.createElement('li');
    item.className = 'wishlist-page__card';

    const imageUrl = product.featured_image
      ? `${product.featured_image.replace(/^\/\//, 'https://')}&width=600`
      : '';

    item.innerHTML = `
      <div class="wishlist-page__card-image-wrapper">
        <a href="${product.url}">
          ${
            imageUrl
              ? `<img class="wishlist-page__card-image" src="${imageUrl}" alt="${product.featured_image_alt || product.title}" loading="lazy" width="600" height="600">`
              : ''
          }
        </a>
        <button
          type="button"
          class="wishlist-page__card-remove button-unstyled"
          data-handle="${product.handle}"
          aria-label="${this.dataset.removeLabel || 'Remove from wishlist'}"
        >
          <span class="svg-wrapper">${this.#heartIcon()}</span>
        </button>
      </div>
      <a href="${product.url}" class="wishlist-page__card-title">${product.title}</a>
      <span class="wishlist-page__card-price">${this.#formatMoney(product.price)}</span>
    `;

    item.querySelector('[data-handle]')?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleWishlist(product.handle);
    });

    return item;
  }

  #heartIcon() {
    return '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M10 17.5s-6.875-4.03-8.86-8.24C-.05 6.24 1.2 3.4 3.9 2.55c1.9-.6 3.9.1 5.1 1.7l1 1.3 1-1.3c1.2-1.6 3.2-2.3 5.1-1.7 2.7.85 3.95 3.69 2.76 6.71C16.875 13.47 10 17.5 10 17.5Z"/></svg>';
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(cents, format, currency);
  }
}

customElements.define('wishlist-page-list', WishlistPageList);
